import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Video, Loader2 } from "lucide-react";
import { useClientRecordSessions } from "@/lib/clientRecordQueries";
import { useCreateSessionRoomLaunch } from "@/lib/sessionQueries";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SessionPrepView from "../SessionPrepView";

export default function PrepTab({ workspaceId, memberId }) {
  const navigate = useNavigate();
  const { data, isPending, isError } = useClientRecordSessions(workspaceId, memberId);
  const [sesId, setSesId] = useState(null);
  const createRoomLaunch = useCreateSessionRoomLaunch(workspaceId);
  const [joining, setJoining] = useState(false);

  const upcoming = (data?.items || []).filter((s) => s.room_state === "booked" || s.room_state === "join_window").sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  useEffect(() => {
    if (upcoming.length && !sesId) setSesId(upcoming[0].ses_id);
  }, [upcoming, sesId]);

  async function handleEnterRoom() {
    setJoining(true);
    try {
      const res = await createRoomLaunch.mutateAsync(sesId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } finally {
      setJoining(false);
    }
  }

  if (isPending) return <p className="text-sm text-ink-soft" data-testid="prep-tab-loading">Loading upcoming sessions…</p>;
  if (isError) return <p className="text-sm text-red-600" data-testid="prep-tab-error">Couldn't load sessions.</p>;

  // Master v5 Prompt 17 coach feedback round 1 — no booked Session is
  // never a blocker: prepare ahead with the same editor in planned mode.
  if (upcoming.length === 0) {
    return (
      <div data-testid="client-360-prep-tab">
        <p className="text-sm text-ink-soft mb-4" data-testid="prep-tab-no-booked-session">
          No Session is booked yet — plan ahead below.
        </p>
        <SessionPrepView key={memberId} workspaceId={workspaceId} planned memberId={memberId} />
      </div>
    );
  }

  return (
    <div data-testid="client-360-prep-tab">
      <div className="flex items-center gap-2 mb-4">
        <p className="text-xs text-ink-soft uppercase">Preparing</p>
        <Select value={sesId || ""} onValueChange={setSesId}>
          <SelectTrigger className="h-8 text-sm w-64" data-testid="prep-tab-session-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {upcoming.map((s) => (
              <SelectItem key={s.ses_id} value={s.ses_id}>{s.title} — {new Date(s.starts_at).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo", month: "short", day: "numeric" })}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {sesId && (
          <button
            onClick={handleEnterRoom}
            disabled={joining}
            className="flex items-center gap-1 text-xs font-medium bg-terracotta text-paper px-3 py-1.5 rounded-full hover:bg-terracotta/90 disabled:opacity-50 shrink-0"
            data-testid="prep-tab-enter-room-btn"
          >
            {joining ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />} Enter the session
          </button>
        )}
      </div>
      {sesId && <SessionPrepView key={sesId} workspaceId={workspaceId} sesId={sesId} />}
    </div>
  );
}
